import React, { useState, useEffect } from 'react';
import { 
  Assembly, 
  AssemblyType, 
  CreateAssemblyDto, 
  UpdateAssemblyDto 
} from '../../types/assembly';
import { assemblyService } from '../../services/assemblyService';
import apiService from '../../services/api';
import { 
  XMarkIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  MapPinIcon,
  LinkIcon,
  ClockIcon,
  UserGroupIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

interface AssemblyFormProps {
  assembly?: Assembly;
  isOpen: boolean;
  onClose: () => void;
  onSave: (assembly: Assembly) => void;
  onError?: (error: string) => void;
}

interface FormData {
  title: string;
  description: string;
  scheduledDate: string;
  scheduledTime: string;
  estimatedDuration: number;
  isVirtual: boolean;
  location: string;
  meetingLink: string;
  quorumRequired: number;
  type: AssemblyType;
  agendaItems: string[];
  selectedComplexes: string[]; // Conjuntos seleccionados para filtrar invitaciones
}

const AssemblyForm: React.FC<AssemblyFormProps> = ({
  assembly,
  isOpen,
  onClose,
  onSave,
  onError
}) => {
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    scheduledDate: '',
    scheduledTime: '',
    estimatedDuration: 120,
    isVirtual: false,
    location: '',
    meetingLink: '',
    quorumRequired: 50,
    type: AssemblyType.ORDINARY,
    agendaItems: [],
    selectedComplexes: [] // Vacío = todos los conjuntos
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [availableComplexes, setAvailableComplexes] = useState<string[]>([]);
  const [alreadyInvitedComplexes, setAlreadyInvitedComplexes] = useState<string[]>([]); // Conjuntos ya invitados (inmutables)

  const isEditMode = !!assembly;

  // Cargar datos del assembly si estamos en modo edición
  useEffect(() => {
    if (assembly) {
      // Cargar selectedComplexes desde el array, con fallback al campo complex
      const selectedComplexes = (assembly as any).selectedComplexes || (assembly.complex ? [assembly.complex] : []);
      
      setFormData({
        title: assembly.title,
        description: assembly.description || '',
        scheduledDate: assembly.scheduledDate,
        scheduledTime: assembly.startTime,
        estimatedDuration: assembly.estimatedDuration,
        isVirtual: assembly.isVirtual,
        location: assembly.location || '',
        meetingLink: assembly.meetingLink || '',
        quorumRequired: parseFloat(assembly.quorumRequired.toString()) || 50, // Parsear decimal a número
        type: assembly.type,
        agendaItems: assembly.agendaItems?.map(item => item.title) || [],
        selectedComplexes
      });
      
      // Guardar conjuntos ya invitados (no se pueden deseleccionar)
      setAlreadyInvitedComplexes(selectedComplexes);
    } else {
      // Resetear formulario para modo creación
      setFormData({
        title: '',
        description: '',
        scheduledDate: '',
        scheduledTime: '',
        estimatedDuration: 120,
        isVirtual: false,
        location: '',
        meetingLink: '',
        quorumRequired: 50,
        type: AssemblyType.ORDINARY,
        agendaItems: [],
        selectedComplexes: []
      });
      setAlreadyInvitedComplexes([]);
    }
    setErrors({});
  }, [assembly]);

  // Cargar conjuntos disponibles
  useEffect(() => {
    const loadComplexes = async () => {
      try {
        const response = await apiService.getComplexes();
        if (response.data.success && response.data.data) {
          const complexNames = response.data.data.map((c: any) => c.name);
          setAvailableComplexes(complexNames);
        }
      } catch (error) {
        console.error('Error cargando conjuntos:', error);
      }
    };
    
    if (isOpen) {
      loadComplexes();
    }
  }, [isOpen]);

  // Validar formulario
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'El título es obligatorio';
    } else if (formData.title.length < 3) {
      newErrors.title = 'El título debe tener al menos 3 caracteres';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'La descripción es obligatoria';
    } else if (formData.description.length < 10) {
      newErrors.description = 'La descripción debe tener al menos 10 caracteres';
    }

    if (!formData.scheduledDate) {
      newErrors.scheduledDate = 'La fecha es obligatoria';
    } else {
      const selectedDate = new Date(`${formData.scheduledDate}T${formData.scheduledTime || '00:00'}`);
      const now = new Date();
      if (selectedDate <= now) {
        newErrors.scheduledDate = 'La fecha debe ser futura';
      }
    }

    if (!formData.scheduledTime) {
      newErrors.scheduledTime = 'La hora es obligatoria';
    }

    if (formData.estimatedDuration < 30 || formData.estimatedDuration > 480) {
      newErrors.estimatedDuration = 'La duración debe estar entre 30 minutos y 8 horas';
    }

    if (formData.quorumRequired < 1 || formData.quorumRequired > 100) {
      newErrors.quorumRequired = 'El quórum debe estar entre 1% y 100%';
    }

    // Validar ubicación solo si no es virtual
    if (!formData.isVirtual && !formData.location.trim()) {
      newErrors.location = 'La ubicación es obligatoria para asambleas presenciales';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (isEditMode && assembly) {
        // Modo edición
        const updateData: UpdateAssemblyDto = {
          title: formData.title,
          description: formData.description,
          scheduledDate: formData.scheduledDate,
          startTime: formData.scheduledTime,
          estimatedDuration: formData.estimatedDuration,
          isVirtual: formData.isVirtual,
          location: formData.isVirtual ? undefined : formData.location,
          meetingLink: formData.isVirtual ? formData.meetingLink || undefined : undefined,
          selectedComplexes: formData.selectedComplexes.length > 0 ? formData.selectedComplexes : undefined,
          quorumRequired: formData.quorumRequired,
          type: formData.type,
          agendaItems: formData.agendaItems.filter(item => item.trim())
        };

        const updatedAssembly = await assemblyService.updateAssembly(assembly.id, updateData);
        onSave(updatedAssembly);
      } else {
        // Modo creación
        const createData: CreateAssemblyDto = {
          title: formData.title,
          description: formData.description,
          scheduledDate: formData.scheduledDate,
          startTime: formData.scheduledTime,
          estimatedDuration: formData.estimatedDuration,
          isVirtual: formData.isVirtual,
          location: formData.isVirtual ? undefined : formData.location,
          meetingLink: formData.isVirtual ? formData.meetingLink || undefined : undefined,
          selectedComplexes: formData.selectedComplexes.length > 0 ? formData.selectedComplexes : undefined,
          quorumRequired: formData.quorumRequired,
          type: formData.type,
          agendaItems: formData.agendaItems.filter(item => item.trim())
        };

        const newAssembly = await assemblyService.createAssembly(createData);
        onSave(newAssembly);
      }

      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al guardar la asamblea';
      setErrors({ general: errorMessage });
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Agregar item de agenda
  const addAgendaItem = () => {
    setFormData({
      ...formData,
      agendaItems: [...formData.agendaItems, '']
    });
  };

  // Actualizar item de agenda
  const updateAgendaItem = (index: number, value: string) => {
    const newItems = [...formData.agendaItems];
    newItems[index] = value;
    setFormData({
      ...formData,
      agendaItems: newItems
    });
  };

  // Eliminar item de agenda
  const removeAgendaItem = (index: number) => {
    const newItems = formData.agendaItems.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      agendaItems: newItems
    });
  };

  // Manejar selección/deselección de conjuntos
  const toggleComplex = (complex: string) => {
    // No permitir deseleccionar conjuntos ya invitados
    if (alreadyInvitedComplexes.includes(complex)) {
      return;
    }
    
    const isSelected = formData.selectedComplexes.includes(complex);
    setFormData({
      ...formData,
      selectedComplexes: isSelected
        ? formData.selectedComplexes.filter(c => c !== complex)
        : [...formData.selectedComplexes, complex]
    });
  };

  // Seleccionar/Deseleccionar todos los conjuntos
  const toggleAllComplexes = () => {
    // Si ya hay conjuntos invitados, no permitir deseleccionarlos todos
    const allSelected = formData.selectedComplexes.length === availableComplexes.length;
    
    setFormData({
      ...formData,
      selectedComplexes: allSelected
        ? [...alreadyInvitedComplexes] // Mantener solo los ya invitados
        : [...availableComplexes] // Seleccionar todos
    });
  };

  // Obtener fecha mínima (hoy)
  const getMinDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4 !mt-0">
      <div className="relative w-full max-w-4xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEditMode ? 'Editar Asamblea' : 'Nueva Asamblea'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="mt-6 space-y-6 px-6 pb-6">
          {/* Error general */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-600">{errors.general}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Columna izquierda */}
            <div className="space-y-6">
              {/* Título */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título *
                </label>
                <div className="relative">
                  <DocumentTextIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.title ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Ej: Asamblea Ordinaria Enero 2024"
                  />
                </div>
                {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.description ? 'border-red-300' : 'border-gray-300'
                  }`}
                  rows={3}
                  placeholder="Descripción de la asamblea..."
                />
                {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
              </div>

              {/* Modalidad */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Modalidad *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div
                    onClick={() => setFormData({ ...formData, isVirtual: false })}
                    className={`cursor-pointer border rounded-lg p-4 text-center transition-all ${
                      !formData.isVirtual 
                        ? 'border-blue-500 bg-blue-50 text-blue-700' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <MapPinIcon className="h-8 w-8 mx-auto mb-2" />
                    <span className="font-medium">Presencial</span>
                  </div>
                  <div
                    onClick={() => setFormData({ ...formData, isVirtual: true })}
                    className={`cursor-pointer border rounded-lg p-4 text-center transition-all ${
                      formData.isVirtual 
                        ? 'border-blue-500 bg-blue-50 text-blue-700' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <LinkIcon className="h-8 w-8 mx-auto mb-2" />
                    <span className="font-medium">Virtual</span>
                  </div>
                </div>
              </div>

              {/* Ubicación o Link de reunión dependiendo de la modalidad */}
              {!formData.isVirtual ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ubicación *
                  </label>
                  <div className="relative">
                    <MapPinIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.location ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Ej: Salón Comunal, Edificio Torre Azul"
                    />
                  </div>
                  {errors.location && <p className="mt-1 text-sm text-red-600">{errors.location}</p>}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Link de Reunión Virtual
                  </label>
                  <div className="relative">
                    <LinkIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="url"
                      value={formData.meetingLink}
                      onChange={(e) => setFormData({ ...formData, meetingLink: e.target.value })}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://meet.google.com/..."
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Columna derecha */}
            <div className="space-y-6">
              {/* Fecha y Hora */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha *
                  </label>
                  <div className="relative">
                    <CalendarDaysIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="date"
                      value={formData.scheduledDate}
                      onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                      min={getMinDate()}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.scheduledDate ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.scheduledDate && <p className="mt-1 text-sm text-red-600">{errors.scheduledDate}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hora *
                  </label>
                  <input
                    type="time"
                    value={formData.scheduledTime}
                    onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.scheduledTime ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.scheduledTime && <p className="mt-1 text-sm text-red-600">{errors.scheduledTime}</p>}
                </div>
              </div>

              {/* Tipo de Asamblea */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Asamblea *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as AssemblyType })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={AssemblyType.ORDINARY}>Ordinaria</option>
                  <option value={AssemblyType.EXTRAORDINARY}>Extraordinaria</option>
                  <option value={AssemblyType.INFORMATIVE}>Informativa</option>
                </select>
              </div>

              {/* Duración estimada */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duración Estimada (minutos) *
                </label>
                <div className="relative">
                  <ClockIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="number"
                    min="30"
                    max="480"
                    value={formData.estimatedDuration}
                    onChange={(e) => setFormData({ ...formData, estimatedDuration: parseInt(e.target.value) || 120 })}
                    className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.estimatedDuration ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="120"
                  />
                </div>
                {errors.estimatedDuration && <p className="mt-1 text-sm text-red-600">{errors.estimatedDuration}</p>}
              </div>

              {/* Quórum Requerido */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quórum Requerido (%) *
                </label>
                <div className="relative">
                  <UserGroupIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.quorumRequired}
                    onChange={(e) => setFormData({ ...formData, quorumRequired: parseInt(e.target.value) || 50 })}
                    className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.quorumRequired ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="50"
                  />
                </div>
                {errors.quorumRequired && <p className="mt-1 text-sm text-red-600">{errors.quorumRequired}</p>}
              </div>
            </div>
          </div>

          {/* Items de Agenda */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Items de Agenda (Opcional)
              </label>
              <button
                type="button"
                onClick={addAgendaItem}
                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Agregar Item
              </button>
            </div>
            
            <div className="space-y-2">
              {formData.agendaItems.map((item, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500 w-8">{index + 1}.</span>
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => updateAgendaItem(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={`Item ${index + 1} de la agenda`}
                  />
                  <button
                    type="button"
                    onClick={() => removeAgendaItem(index)}
                    className="p-2 text-red-600 hover:text-red-800 focus:outline-none"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
          {/* Selector de Conjuntos (si hay conjuntos disponibles) */}
          {availableComplexes.length > 0 && (
            <div className="border-t pt-6">
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filtrar Invitaciones por Conjunto (Opcional)
                </label>
                <p className="text-sm text-gray-500 mb-3">
                  Selecciona uno o más conjuntos para enviar invitaciones solo a sus propietarios. 
                  Si no seleccionas ninguno, se invitará a todos.
                </p>
                {isEditMode && alreadyInvitedComplexes.length > 0 && (
                  <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">
                      <span className="font-medium">ℹ️ Nota:</span> Los conjuntos marcados con "✓ Invitado" ya recibieron la invitación y no pueden ser desinvitados.
                    </p>
                  </div>
                )}
                <button
                  type="button"
                  onClick={toggleAllComplexes}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  {formData.selectedComplexes.length === availableComplexes.length
                    ? 'Deseleccionar Todos'
                    : 'Seleccionar Todos'}
                </button>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-60 overflow-y-auto p-3 bg-gray-50 rounded-lg">
                {availableComplexes.map((complex) => {
                  const isSelected = formData.selectedComplexes.includes(complex);
                  const isAlreadyInvited = alreadyInvitedComplexes.includes(complex);
                  
                  return (
                    <label
                      key={complex}
                      className={`flex items-center p-3 border-2 rounded-lg transition-all ${
                        isAlreadyInvited
                          ? 'border-green-300 bg-green-50 cursor-not-allowed'
                          : isSelected
                          ? 'border-blue-500 bg-blue-50 cursor-pointer'
                          : 'border-gray-200 hover:border-gray-300 bg-white cursor-pointer'
                      }`}
                      title={isAlreadyInvited ? 'Este conjunto ya fue invitado y no se puede desinvitar' : ''}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleComplex(complex)}
                        disabled={isAlreadyInvited}
                        className={`h-4 w-4 focus:ring-blue-500 border-gray-300 rounded ${
                          isAlreadyInvited
                            ? 'text-green-600 cursor-not-allowed opacity-60'
                            : 'text-blue-600 cursor-pointer'
                        }`}
                      />
                      <span className={`ml-3 text-sm flex items-center gap-2 ${
                        isAlreadyInvited 
                          ? 'font-medium text-green-800' 
                          : isSelected 
                          ? 'font-medium text-blue-900' 
                          : 'text-gray-700'
                      }`}>
                        {complex}
                        {isAlreadyInvited && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            ✓ Invitado
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
              {formData.selectedComplexes.length > 0 && (
                <p className="mt-2 text-sm text-green-600">
                  ✓ {formData.selectedComplexes.length} conjunto(s) seleccionado(s)
                </p>
              )}
            </div>
          )}
          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Guardando...
                </div>
              ) : (
                isEditMode ? 'Actualizar' : 'Crear Asamblea'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssemblyForm;